import React from 'react';
import { SectionCard, TextField, TextAreaField, ImageField, NumberField, Repeater } from '../../../components/admin/EditorFields';

const ICONS = ['Leaf', 'Truck', 'Cloud', 'Wallet', 'Clock', 'Sparkles', 'Droplets', 'MapPin', 'AlertTriangle', 'Recycle', 'Calendar', 'Trophy', 'Heart', 'Droplet', 'Award', 'Sun', 'Users', 'Shield'];

export default function HomeEditor({ data, setData }) {
  const setSection = (key, value) => setData((d) => ({ ...d, [key]: { ...(d[key] || {}), ...value } }));
  const upd = (path, value) => setData((d) => {
    const next = JSON.parse(JSON.stringify(d));
    const keys = path.split('.');
    let cur = next;
    for (let i = 0; i < keys.length - 1; i++) {
      cur[keys[i]] = cur[keys[i]] || {};
      cur = cur[keys[i]];
    }
    cur[keys[keys.length - 1]] = value;
    return next;
  });

  return (
    <div className="space-y-5">
      {/* HERO */}
      <SectionCard title="Section Hero (en-tête)">
        <TextField label="Badge (au-dessus du titre)" value={data.hero?.badge} onChange={(v) => upd('hero.badge', v)} />
        <div className="grid sm:grid-cols-2 gap-3">
          <TextField label="Titre - ligne 1" value={data.hero?.titleLine1} onChange={(v) => upd('hero.titleLine1', v)} />
          <TextField label="Titre - ligne 2 (en couleur)" value={data.hero?.titleLine2} onChange={(v) => upd('hero.titleLine2', v)} />
        </div>
        <TextField label="Sous-titre" value={data.hero?.subtitle} onChange={(v) => upd('hero.subtitle', v)} />
        <TextAreaField label="Description (HTML autorisé)" rows={3} value={data.hero?.description} onChange={(v) => upd('hero.description', v)} />
        <div className="grid sm:grid-cols-2 gap-3">
          <TextField label="CTA principal - libellé" value={data.hero?.primaryCtaLabel} onChange={(v) => upd('hero.primaryCtaLabel', v)} />
          <TextField label="CTA principal - lien" value={data.hero?.primaryCtaLink} onChange={(v) => upd('hero.primaryCtaLink', v)} />
          <TextField label="CTA secondaire - libellé" value={data.hero?.secondaryCtaLabel} onChange={(v) => upd('hero.secondaryCtaLabel', v)} />
          <TextField label="CTA secondaire - lien" value={data.hero?.secondaryCtaLink} onChange={(v) => upd('hero.secondaryCtaLink', v)} />
        </div>
        <ImageField label="Image principale du hero" value={data.hero?.image} onChange={(v) => upd('hero.image', v)} />
        <Repeater
          label="Badges de confiance (sous le hero)"
          items={data.hero?.trustBadges}
          onChange={(items) => upd('hero.trustBadges', items)}
          newItem={() => ({ icon: 'Leaf', text: '' })}
          renderItem={(item, update) => (
            <div className="grid sm:grid-cols-2 gap-3">
              <IconSelect value={item.icon} onChange={(v) => update({ icon: v })} />
              <TextField label="Texte" value={item.text} onChange={(v) => update({ text: v })} />
            </div>
          )}
        />
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="p-3 rounded-xl border border-sky-100 bg-sky-50/30 space-y-2">
            <p className="text-xs font-bold uppercase text-slate-500">Carte flottante 1</p>
            <TextField label="Label" value={data.hero?.floatStat1?.label} onChange={(v) => upd('hero.floatStat1.label', v)} />
            <TextField label="Valeur" value={data.hero?.floatStat1?.value} onChange={(v) => upd('hero.floatStat1.value', v)} />
            <TextField label="Sous-texte" value={data.hero?.floatStat1?.sub} onChange={(v) => upd('hero.floatStat1.sub', v)} />
          </div>
          <div className="p-3 rounded-xl border border-sky-100 bg-sky-50/30 space-y-2">
            <p className="text-xs font-bold uppercase text-slate-500">Carte flottante 2</p>
            <TextField label="Label" value={data.hero?.floatStat2?.label} onChange={(v) => upd('hero.floatStat2.label', v)} />
            <TextField label="Valeur" value={data.hero?.floatStat2?.value} onChange={(v) => upd('hero.floatStat2.value', v)} />
            <TextField label="Sous-texte" value={data.hero?.floatStat2?.sub} onChange={(v) => upd('hero.floatStat2.sub', v)} />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <TextField label="Légende image - haut" value={data.hero?.imageCaption1} onChange={(v) => upd('hero.imageCaption1', v)} />
          <TextField label="Légende image - bas" value={data.hero?.imageCaption2} onChange={(v) => upd('hero.imageCaption2', v)} />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <TextField label="Carte accent - label" value={data.hero?.accentLabel} onChange={(v) => upd('hero.accentLabel', v)} />
          <TextField label="Carte accent - valeur" value={data.hero?.accentValue} onChange={(v) => upd('hero.accentValue', v)} />
        </div>
      </SectionCard>

      {/* STATS */}
      <SectionCard title="Section Statistiques">
        <TextField label="Eyebrow" value={data.stats?.eyebrow} onChange={(v) => upd('stats.eyebrow', v)} />
        <div className="grid sm:grid-cols-2 gap-3">
          <TextField label="Titre" value={data.stats?.title} onChange={(v) => upd('stats.title', v)} />
          <TextField label="Titre en couleur" value={data.stats?.titleHighlight} onChange={(v) => upd('stats.titleHighlight', v)} />
        </div>
        <TextAreaField label="Description" rows={2} value={data.stats?.description} onChange={(v) => upd('stats.description', v)} />
        <Repeater
          label="Statistiques (6 cartes)"
          items={data.stats?.items}
          onChange={(items) => upd('stats.items', items)}
          newItem={() => ({ value: 0, suffix: '', label: '', sublabel: '' })}
          renderItem={(item, update) => (
            <div className="grid sm:grid-cols-2 gap-3">
              <NumberField label="Valeur" value={item.value} onChange={(v) => update({ value: v })} step={0.1} />
              <TextField label="Suffixe (%, M, km...)" value={item.suffix} onChange={(v) => update({ suffix: v })} />
              <TextField label="Label" value={item.label} onChange={(v) => update({ label: v })} />
              <TextField label="Sous-label" value={item.sublabel} onChange={(v) => update({ sublabel: v })} />
            </div>
          )}
        />
        <TextField label="Conclusion (titre)" value={data.stats?.footer} onChange={(v) => upd('stats.footer', v)} />
        <TextAreaField label="Conclusion (texte)" rows={2} value={data.stats?.footerSub} onChange={(v) => upd('stats.footerSub', v)} />
      </SectionCard>

      {/* VIDEO INTRO — image d'aperçu (poster) */}
      <SectionCard title="Section Vidéo Journal (image d'aperçu)">
        <p className="text-xs text-slate-500 -mt-2 mb-3">
          Uploade une image qui s'affichera à la place du fond noir avant que l'utilisateur clique sur Play.
          <br />Astuce : fais une capture d'écran de la vidéo à 00:01 (format paysage 16:9 recommandé, min 1280×720).
        </p>
        <ImageField label="Image d'aperçu de la vidéo (poster)" value={data.videoIntro?.previewImage} onChange={(v) => upd('videoIntro.previewImage', v)} />
      </SectionCard>

      {/* ABOUT */}
      <SectionCard title="Section « Notre histoire »">
        <TextField label="Eyebrow" value={data.about?.eyebrow} onChange={(v) => upd('about.eyebrow', v)} />
        <div className="grid sm:grid-cols-2 gap-3">
          <TextField label="Titre" value={data.about?.title} onChange={(v) => upd('about.title', v)} />
          <TextField label="Titre en couleur" value={data.about?.titleHighlight} onChange={(v) => upd('about.titleHighlight', v)} />
        </div>
        <TextAreaField label="Paragraphe 1 (HTML autorisé)" rows={3} value={data.about?.paragraph1} onChange={(v) => upd('about.paragraph1', v)} />
        <TextAreaField label="Paragraphe 2" rows={3} value={data.about?.paragraph2} onChange={(v) => upd('about.paragraph2', v)} />
        <ImageField label="Image" value={data.about?.image} onChange={(v) => upd('about.image', v)} />
        <div className="grid sm:grid-cols-2 gap-3">
          <TextField label="Bulle - titre" value={data.about?.convictionTitle} onChange={(v) => upd('about.convictionTitle', v)} />
          <TextField label="Bulle - texte" value={data.about?.convictionText} onChange={(v) => upd('about.convictionText', v)} />
          <TextField label="CTA principal" value={data.about?.primaryCtaLabel} onChange={(v) => upd('about.primaryCtaLabel', v)} />
          <TextField label="Lien CTA principal" value={data.about?.primaryCtaLink} onChange={(v) => upd('about.primaryCtaLink', v)} />
          <TextField label="CTA secondaire" value={data.about?.secondaryCtaLabel} onChange={(v) => upd('about.secondaryCtaLabel', v)} />
          <TextField label="Lien CTA secondaire" value={data.about?.secondaryCtaLink} onChange={(v) => upd('about.secondaryCtaLink', v)} />
        </div>
      </SectionCard>

      {/* BENEFITS */}
      <SectionCard title="Section Avantages (Triple zéro)">
        <TextField label="Eyebrow" value={data.benefits?.eyebrow} onChange={(v) => upd('benefits.eyebrow', v)} />
        <div className="grid sm:grid-cols-2 gap-3">
          <TextField label="Titre" value={data.benefits?.title} onChange={(v) => upd('benefits.title', v)} />
          <TextField label="Titre en couleur" value={data.benefits?.titleHighlight} onChange={(v) => upd('benefits.titleHighlight', v)} />
        </div>
        <TextAreaField label="Description" rows={2} value={data.benefits?.description} onChange={(v) => upd('benefits.description', v)} />
        <Repeater
          label="Avantages"
          items={data.benefits?.items}
          onChange={(items) => upd('benefits.items', items)}
          newItem={() => ({ icon: 'Leaf', title: '', description: '' })}
          renderItem={(item, update) => (
            <div className="space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <IconSelect value={item.icon} onChange={(v) => update({ icon: v })} />
                <TextField label="Titre" value={item.title} onChange={(v) => update({ title: v })} />
              </div>
              <TextAreaField label="Description" rows={2} value={item.description} onChange={(v) => update({ description: v })} />
            </div>
          )}
        />
      </SectionCard>

      {/* CALCULATOR */}
      <SectionCard title="Section Calculateur d'impact">
        <TextField label="Eyebrow" value={data.calculator?.eyebrow} onChange={(v) => upd('calculator.eyebrow', v)} />
        <div className="grid sm:grid-cols-2 gap-3">
          <TextField label="Titre" value={data.calculator?.title} onChange={(v) => upd('calculator.title', v)} />
          <TextField label="Titre en couleur" value={data.calculator?.titleHighlight} onChange={(v) => upd('calculator.titleHighlight', v)} />
        </div>
        <TextAreaField label="Description" rows={2} value={data.calculator?.description} onChange={(v) => upd('calculator.description', v)} />
      </SectionCard>

      {/* STEPS */}
      <SectionCard title="Section Étapes (Comment ça marche)">
        <TextField label="Eyebrow" value={data.steps?.eyebrow} onChange={(v) => upd('steps.eyebrow', v)} />
        <div className="grid sm:grid-cols-2 gap-3">
          <TextField label="Titre" value={data.steps?.title} onChange={(v) => upd('steps.title', v)} />
          <TextField label="Titre en couleur" value={data.steps?.titleHighlight} onChange={(v) => upd('steps.titleHighlight', v)} />
        </div>
        <Repeater
          label="Étapes"
          items={data.steps?.items}
          onChange={(items) => upd('steps.items', items)}
          newItem={() => ({ number: '0X', title: '', description: '' })}
          renderItem={(item, update) => (
            <div className="space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <TextField label="Numéro" value={item.number} onChange={(v) => update({ number: v })} />
                <TextField label="Titre" value={item.title} onChange={(v) => update({ title: v })} />
              </div>
              <TextAreaField label="Description" rows={2} value={item.description} onChange={(v) => update({ description: v })} />
            </div>
          )}
        />
      </SectionCard>

      {/* TESTIMONIALS */}
      <SectionCard title="Témoignages">
        <TextField label="Eyebrow" value={data.testimonials?.eyebrow} onChange={(v) => upd('testimonials.eyebrow', v)} />
        <TextField label="Titre" value={data.testimonials?.title} onChange={(v) => upd('testimonials.title', v)} />
        <TextAreaField label="Description" rows={2} value={data.testimonials?.description} onChange={(v) => upd('testimonials.description', v)} />
        <Repeater
          label="Témoignages"
          items={data.testimonials?.items}
          onChange={(items) => upd('testimonials.items', items)}
          newItem={() => ({ name: '', city: '', rating: 5, text: '', avatar: '' })}
          renderItem={(item, update) => (
            <div className="space-y-3">
              <div className="grid sm:grid-cols-3 gap-3">
                <TextField label="Nom" value={item.name} onChange={(v) => update({ name: v })} />
                <TextField label="Ville" value={item.city} onChange={(v) => update({ city: v })} />
                <TextField label="Initiales" value={item.avatar} onChange={(v) => update({ avatar: v })} />
              </div>
              <NumberField label="Note (1-5)" value={item.rating} onChange={(v) => update({ rating: v })} />
              <TextAreaField label="Témoignage" rows={3} value={item.text} onChange={(v) => update({ text: v })} />
            </div>
          )}
        />
      </SectionCard>

      {/* PARTNERS */}
      <SectionCard title="Partenaires (3 catégories)">
        <TextField label="Titre historique (legacy)" hint="Non utilisé — laissez tel quel" value={data.partners?.label} onChange={(v) => upd('partners.label', v)} />
        <Repeater
          label="Groupes de partenaires (Financiers, Techniques, Communication…)"
          items={data.partners?.groups}
          onChange={(items) => upd('partners.groups', items)}
          newItem={() => ({ title: 'Nouveau groupe', subtitle: '', items: [] })}
          addLabel="Ajouter un groupe"
          renderItem={(group, updateGroup) => (
            <div className="space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <TextField label="Titre du groupe" value={group.title} onChange={(v) => updateGroup({ title: v })} />
                <TextField label="Sous-titre (optionnel)" value={group.subtitle} onChange={(v) => updateGroup({ subtitle: v })} />
              </div>
              <Repeater
                label="Partenaires de ce groupe"
                items={group.items}
                onChange={(items) => updateGroup({ items })}
                newItem={() => ({ name: '', role: '', logo: '' })}
                addLabel="Ajouter un partenaire"
                renderItem={(p, updateP) => (
                  <div className="space-y-3">
                    <div className="grid sm:grid-cols-2 gap-3">
                      <TextField label="Nom" value={p.name} onChange={(v) => updateP({ name: v })} />
                      <TextField label="Rôle / description" value={p.role} onChange={(v) => updateP({ role: v })} />
                    </div>
                    <ImageField label="Logo" value={p.logo} onChange={(v) => updateP({ logo: v })} />
                  </div>
                )}
              />
            </div>
          )}
        />
      </SectionCard>

      {/* FINAL CTA */}
      <SectionCard title="Section finale (CTA Don)">
        <TextField label="Eyebrow" value={data.cta?.eyebrow} onChange={(v) => upd('cta.eyebrow', v)} />
        <div className="grid sm:grid-cols-3 gap-3">
          <TextField label="Titre - début" value={data.cta?.title} onChange={(v) => upd('cta.title', v)} />
          <TextField label="Titre en couleur" value={data.cta?.titleHighlight} onChange={(v) => upd('cta.titleHighlight', v)} />
          <TextField label="Titre - fin" value={data.cta?.titleSuffix} onChange={(v) => upd('cta.titleSuffix', v)} />
        </div>
        <TextAreaField label="Description" rows={2} value={data.cta?.description} onChange={(v) => upd('cta.description', v)} />
        <div className="grid sm:grid-cols-2 gap-3">
          <TextField label="CTA principal" value={data.cta?.primaryCtaLabel} onChange={(v) => upd('cta.primaryCtaLabel', v)} />
          <TextField label="Lien CTA principal" value={data.cta?.primaryCtaLink} onChange={(v) => upd('cta.primaryCtaLink', v)} />
          <TextField label="CTA secondaire (HelloAsso)" value={data.cta?.secondaryCtaLabel} onChange={(v) => upd('cta.secondaryCtaLabel', v)} />
        </div>
      </SectionCard>

      {/* BLOG TEASER */}
      <SectionCard title="Aperçu du Blog">
        <TextField label="Eyebrow" value={data.blogTeaser?.eyebrow} onChange={(v) => upd('blogTeaser.eyebrow', v)} />
        <TextField label="Titre" value={data.blogTeaser?.title} onChange={(v) => upd('blogTeaser.title', v)} />
      </SectionCard>
    </div>
  );
}

function IconSelect({ value, onChange }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">Icône</label>
      <select value={value || 'Leaf'} onChange={(e) => onChange(e.target.value)} className="w-full h-11 rounded-xl border border-sky-200 px-3 text-sm">
        {ICONS.map((i) => <option key={i} value={i}>{i}</option>)}
      </select>
    </div>
  );
}
